// Keyboard + mouse input for the tank. Matches WinBolo's classic control
// scheme: keyboard for tank driving and firing, mouse for target placement
// (builder actions, pillbox drop). The tool picker controls what a map
// click does.

export type Tool = "none" | "tree" | "road" | "wall" | "pillbox" | "mine";

export interface InputState {
  throttle: number;
  turn: number;
  fire: boolean;
  laying: boolean;
  sightUp: boolean;
  sightDown: boolean;
}

export type Action =
  // Edge-triggered "deploy builder to tile in front of tank" shortcut (B).
  | { kind: "builder-front" }
  // Edge-triggered "drop pillbox in front of tank" shortcut (P) for
  // keyboard users who don't want to mouse-click.
  | { kind: "pillbox-drop-front" }
  // Toggle the in-game scoreboard overlay (N).
  | { kind: "toggle-scoreboard" }
  // Edge-triggered "cycle the spectated player" (Left/Right arrow). Only
  // acted on by the game screen while the local tank is dead; ignored while
  // alive (the arrows still drive the tank as usual).
  | { kind: "spectate-cycle"; dir: 1 | -1 }
  // Map click at a tile, with the currently selected tool.
  | { kind: "map-click"; tileX: number; tileY: number; tool: Tool };

export interface MouseHover {
  tileX: number;
  tileY: number;
}

export class InputController {
  private keys = new Set<string>();
  private actions: Action[] = [];
  private tool: Tool = "none";
  private hover: MouseHover | null = null;
  private canvas: HTMLCanvasElement | null = null;
  // Camera offset in world pixels; the game screen updates this each frame
  // so we can translate screen -> world -> tile coords on mouse events.
  private camX = 0;
  private camY = 0;
  private scale = 1;

  // Lets the game screen react to tool changes (update toolbar UI).
  onToolChange?: (tool: Tool) => void;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  constructor() {
    this.onKeyDown = (e) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      const k = e.key;
      // Tool-select hotkeys: 1=tree, 2=road, 3=wall, 4=pillbox, 5=mine, 0/Esc=none.
      // These are always active — even when not holding a driving key —
      // because switching tools mid-drive is the common case.
      if (k === "1") { this.setTool("tree");    e.preventDefault(); return; }
      if (k === "2") { this.setTool("road");    e.preventDefault(); return; }
      if (k === "3") { this.setTool("wall");    e.preventDefault(); return; }
      if (k === "4") { this.setTool("pillbox"); e.preventDefault(); return; }
      if (k === "5") { this.setTool("mine");    e.preventDefault(); return; }
      if (k === "0" || k === "Escape") { this.setTool("none"); e.preventDefault(); return; }
      if (k === "n" || k === "N") { this.actions.push({ kind: "toggle-scoreboard" }); e.preventDefault(); return; }

      if (this.isGameKey(k)) {
        e.preventDefault();
        if (!this.keys.has(k)) {
          if (k === "b" || k === "B") this.actions.push({ kind: "builder-front" });
          else if (k === "p" || k === "P") this.actions.push({ kind: "pillbox-drop-front" });
          // Edge-triggered spectate cycling (one step per fresh press). The
          // game screen only acts on these while the local tank is dead.
          else if (k === "ArrowLeft") this.actions.push({ kind: "spectate-cycle", dir: -1 });
          else if (k === "ArrowRight") this.actions.push({ kind: "spectate-cycle", dir: 1 });
        }
        this.keys.add(k);
      }
    };
    this.onKeyUp = (e) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      if (this.isGameKey(e.key)) {
        this.keys.delete(e.key);
        e.preventDefault();
      }
    };
    this.onBlur = () => this.keys.clear();

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
  }

  attachCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    canvas.addEventListener("mousemove", (e) => this.onMouseMove(e));
    canvas.addEventListener("mouseleave", () => { this.hover = null; });
    canvas.addEventListener("mousedown", (e) => this.onMouseDown(e));
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  setCamera(camX: number, camY: number, scale = 1): void {
    this.camX = camX;
    this.camY = camY;
    this.scale = scale;
  }

  setTool(tool: Tool): void {
    if (this.tool === tool) return;
    this.tool = tool;
    this.onToolChange?.(tool);
  }

  getTool(): Tool {
    return this.tool;
  }

  getHover(): MouseHover | null {
    return this.hover;
  }

  sample(): InputState {
    const up = this.keys.has("ArrowUp") || this.keys.has("w") || this.keys.has("W");
    const down = this.keys.has("ArrowDown") || this.keys.has("s") || this.keys.has("S");
    const left = this.keys.has("ArrowLeft") || this.keys.has("a") || this.keys.has("A");
    const right = this.keys.has("ArrowRight") || this.keys.has("d") || this.keys.has("D");
    return {
      throttle: (up ? 1 : 0) + (down ? -1 : 0),
      turn: (right ? 1 : 0) + (left ? -1 : 0),
      fire: this.keys.has(" "),
      laying: this.keys.has("Tab"),
      sightUp: this.keys.has("]"),
      sightDown: this.keys.has("["),
    };
  }

  drainActions(): Action[] {
    const out = this.actions;
    this.actions = [];
    return out;
  }

  private onMouseMove(e: MouseEvent): void {
    const canvas = this.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const cx = (e.clientX - rect.left) * scaleX;
    const cy = (e.clientY - rect.top) * scaleY;
    const wx = cx / this.scale + this.camX;
    const wy = cy / this.scale + this.camY;
    const TILE = 16;
    this.hover = { tileX: Math.floor(wx / TILE), tileY: Math.floor(wy / TILE) };
  }

  private onMouseDown(e: MouseEvent): void {
    if (!this.hover) return;
    // Left click fires tool; any other button is ignored (we might wire
    // right-click to "cancel tool" later).
    if (e.button !== 0) return;
    if (this.tool === "none") return;
    e.preventDefault();
    this.actions.push({
      kind: "map-click",
      tileX: this.hover.tileX,
      tileY: this.hover.tileY,
      tool: this.tool,
    });
  }

  private isGameKey(k: string): boolean {
    return (
      k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight" ||
      k === "w" || k === "W" || k === "a" || k === "A" ||
      k === "s" || k === "S" || k === "d" || k === "D" ||
      k === "b" || k === "B" ||
      k === "p" || k === "P" ||
      k === " " || k === "Tab" ||
      k === "[" || k === "]"
    );
  }
}
